import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import { brand } from "../../../theme/businessFlowsTheme";
import { Hint, Refusal, Toast, focusRing } from "../../workspaces/WorkspaceUi";
import { administersDatabase, canPlatform, startingDatabase, viewDatabases } from "../../../utils/permissions";
import { getViewsOverview, refreshViewsCatalogue } from "../../../actions";
import { useViewConfigRefresh } from "../../app/ViewConfigContext";
import DatabaseSelect from "../config/DatabaseSelect";
import ConfirmDialog from "./ConfirmDialog";
import { SAVED, USERS_SEE_IT } from "./names";
import FieldsTab from "./FieldsTab";
import ObjectTypesTab from "./ObjectTypesTab";
import RelationshipsTab from "./RelationshipsTab";
import SettingsTab from "./SettingsTab";
import CardPreview from "./CardPreview";

// The tabs of Console -> Display. Every tab gets the same props.
const TABS = [
  { key: "types", label: "Object types", Component: ObjectTypesTab },
  { key: "relationships", label: "Relationships", Component: RelationshipsTab },
  { key: "fields", label: "Fields", Component: FieldsTab },
  { key: "settings", label: "Settings", Component: SettingsTab },
];

// What a tab's own confirmation becomes: what changed, then when users see it.
const confirmation = (message) => (message ? `${message} ${USERS_SEE_IT}` : SAVED);

// The database this screen opens on when a link names one:
// /admin/display?database=acme. An unknown name is ignored.
const databaseInLink = () => {
  try {
    return new URLSearchParams(window.location.search).get("database") || "";
  } catch (err) {
    return "";
  }
};

// How one database looks to everyone who reads it: what its object types and
// relationships are called, which of them the explorer, search and maps show,
// which fields each place lists, and the rest of its display settings.
//
// This is not the workspace's Views, which is a whitelist per group. The two
// are named apart on purpose: a group's view narrows what its people may reach,
// this screen decides how whatever they reach is shown.
export default function DisplayPage({ user = {} }) {
  // Staff: every database; customers: those of the workspaces they administer.
  const databases = useMemo(() => viewDatabases(user), [user]);
  const labelFor = useCallback((name) => user.databaseLabels?.[name] || name, [user.databaseLabels]);
  const refreshCurrent = useViewConfigRefresh();
  // The database this browser tab is using: saving there refreshes what the app shows.
  const currentDatabase = localStorage.getItem("db") || user.mainDatabase || "";

  // Where a group's whitelist is edited: staff reach every workspace from the
  // console, a Workspace admin their own from Manage workspace.
  const viewsLink = canPlatform(user, "tenants.view") ? "/admin/workspaces" : "/workspace";
  const [database, setDatabase] = useState(() => {
    const asked = databaseInLink();
    return databases.includes(asked) ? asked : startingDatabase(user, databases);
  });
  const mayManage = administersDatabase(user, database);
  const [tab, setTab] = useState(TABS[0].key);
  const [overview, setOverview] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  // `notice` is the theme's palette elsewhere, so the toast's text is `toast`.
  const [toast, setToast] = useState("");
  const [previewType, setPreviewType] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  // Only the newest read may land, and only for the database still chosen: a
  // late answer for another database (or an older read) is dropped.
  const readCount = useRef(0);
  const databaseNow = useRef(database);
  databaseNow.current = database;

  const load = useCallback(async () => {
    if (!database) return;
    const ticket = ++readCount.current;
    setOverview(null);
    setLoadError("");
    const res = await getViewsOverview(database);
    if (ticket !== readCount.current) return;
    if (res?.success && Array.isArray(res.content?.types)) setOverview(res.content);
    else setLoadError(res?.msg || "Couldn't read how this database is shown. Try again in a moment.");
  }, [database]);

  // A quiet re-read after a change: the tabs stay on screen meanwhile.
  const reread = useCallback(async () => {
    if (!database) return;
    const ticket = ++readCount.current;
    const res = await getViewsOverview(database);
    if (ticket !== readCount.current) return;
    if (res?.success && Array.isArray(res.content?.types)) setOverview(res.content);
  }, [database]);

  // Types and relationships are discovered with two full scans and kept for
  // five minutes, so one an import wrote a moment ago is missing from this
  // catalogue until then. This reads the database again now, and answers the
  // new overview. Asked twice within ten seconds it says so - a note, not a
  // failure, and nothing is scanned twice.
  const refreshCatalogue = useCallback(async () => {
    if (!database) return;
    setRefreshing(true);
    setError("");
    const ticket = ++readCount.current;
    const res = await refreshViewsCatalogue(database);
    setRefreshing(false);
    if (res?.success && Array.isArray(res.content?.types)) {
      if (ticket === readCount.current) setOverview(res.content);
      if (database === currentDatabase) refreshCurrent();
    } else if (res?.success === false && res?.reason !== "too-many-attempts") {
      setError(res?.msg || "Couldn't read this database's types again. Try again in a moment.");
      return;
    }
    setToast(res?.msg || "Up to date.");
  }, [database, currentDatabase, refreshCurrent]);

  useEffect(() => {
    setError("");
    setPreviewType("");
    load();
    // Nothing read for this database may land after it was left.
    const reads = readCount;
    return () => {
      reads.current += 1;
    };
  }, [load]);

  // Every change answers with the new overview, so the screen and the preview
  // update at once; pages open on this database follow within 30 seconds, and
  // this browser's own pages right away. A change that was saved but couldn't
  // be read back answers without it: its message is shown and the overview
  // read again. Answers `{ ok, msg }`; with `inline` a refusal is left to the
  // caller (a dialog shows it where the admin acted).
  const apply = useCallback(
    async (request, message, { inline = false } = {}) => {
      const forDatabase = database;
      setError("");
      const res = await request();
      const stillHere = databaseNow.current === forDatabase;
      if (res?.success) {
        if (!stillHere) return { ok: true, msg: "" };
        if (res.content === null) {
          // Saved, but the server couldn't read the overview back: its message
          // says so, and the overview is read again.
          setToast(res.msg || SAVED);
          reread();
        } else {
          if (Array.isArray(res.content?.types)) {
            readCount.current += 1;
            setOverview(res.content);
          }
          setToast(confirmation(message));
        }
        if (forDatabase === currentDatabase) refreshCurrent();
        return { ok: true, msg: "" };
      }
      const msg = res?.msg || "That change didn't save. Try again in a moment.";
      if (!inline && stillHere) setError(msg);
      return { ok: false, msg };
    },
    [database, currentDatabase, refreshCurrent, reread]
  );

  // A tab asks before anything a person would want to think about: a reset,
  // or a switch turned off for every type at once. `confirmLabel` lets the
  // dialog's button say what will actually happen.
  const askReset = useCallback(({ title, text, request, message, confirmLabel, confirmIcon }) => {
    setConfirmError("");
    setConfirm({ title, text, request, message, confirmLabel, confirmIcon });
  }, []);

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    setConfirmError("");
    const result = await apply(confirm.request, confirm.message, { inline: true });
    setConfirmBusy(false);
    if (result.ok) setConfirm(null);
    else setConfirmError(result.msg);
  };

  const current = TABS.find((t) => t.key === tab) || TABS[0];
  const TabComponent = current.Component;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, ...focusRing }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: brand.ink, color: brand.signal, flexShrink: 0 }}>
          <PaletteOutlinedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Display
          </Typography>
          <Typography variant="body2" color="text.secondary">
            How this database looks: names, colours, fields and where each object type shows. It applies to everyone who reads the database. Changes reach
            open pages within 30 seconds.
          </Typography>
        </Box>
        <DatabaseSelect databases={databases} labelFor={labelFor} value={database} onChange={setDatabase} sx={{ width: { xs: "100%", sm: 240 } }} />
        <Tooltip title="Read this database's object types and relationships again. New ones can take a few minutes to appear on their own.">
          <span>
            <IconButton size="small" disabled={!database || refreshing} onClick={refreshCatalogue} aria-label="Refresh the types and relationships">
              {refreshing ? <CircularProgress size={18} /> : <RefreshRoundedIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Which object types a person may reach is a different decision - the view they hold, made in the workspace's{" "}
        <Link href={viewsLink} underline="hover" sx={{ fontWeight: 600, color: brand.ink }}>
          Views
        </Link>
        .
      </Typography>

      {!mayManage && (
        <Hint sx={{ mb: 2 }} next="Changing it is for bpExperts and this workspace's admins.">
          You can read how this database is shown, and not change it.
        </Hint>
      )}
      {error && (
        <Refusal sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Refusal>
      )}

      <Tabs
        value={current.key}
        onChange={(e, value) => setTab(value)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{
          mb: 2,
          borderBottom: `1px solid ${brand.line}`,
          "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
          "& .MuiTabs-indicator": { bgcolor: brand.ink, height: 3, borderRadius: 3 },
          "& .Mui-selected": { color: `${brand.ink} !important` },
        }}
      >
        {TABS.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} />
        ))}
      </Tabs>

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 340px" }, alignItems: "start" }}>
        <Box sx={{ minWidth: 0 }}>
          {loadError ? (
            <Refusal
              action={
                <Button size="small" onClick={load} sx={{ flexShrink: 0 }}>
                  Retry
                </Button>
              }
            >
              {loadError}
            </Refusal>
          ) : overview === null ? (
            <Stack spacing={1.5}>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: 3 }} />
              ))}
            </Stack>
          ) : (
            <TabComponent
              user={user}
              overview={overview}
              database={database}
              mayManage={mayManage}
              apply={apply}
              askReset={askReset}
              previewType={previewType}
              onPreview={setPreviewType}
            />
          )}
        </Box>
        <Box sx={{ position: { lg: "sticky" }, top: { lg: 16 } }}>
          <CardPreview config={overview} objectType={previewType} />
        </Box>
      </Box>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        text={confirm?.text}
        confirmLabel={confirm?.confirmLabel}
        confirmIcon={confirm?.confirmIcon}
        busy={confirmBusy}
        error={confirmError}
        onConfirm={runConfirm}
        onClose={() => !confirmBusy && setConfirm(null)}
      />
      <Toast notice={toast} onClose={() => setToast("")} />
    </Box>
  );
}
